import {
  Text,
  Heading,
  Stack,
  Columns,
  Column,
  Box,
  Strong,
  List,
  ContentBlock,
  Divider,
} from 'braid-design-system';
import React from 'react';

import { StaticPageProvider, CTAButton } from 'src/components';
import { CREATE_JOB, REGISTER } from 'src/shared/links';
import type { PageProps } from 'src/shared/types';

import { PageIntro } from '../../components';

import * as styles from './App.css';

const StaticPageProps: PageProps = {
  title: 'Talent Search',
  returnUrl: '/products/talentsearch',
  activeTab: 'Products',
  activeSubTab: 'Talent search',
  siteSection: 'products',
  currentPage: 'talent-search',
  eventName: 'talent-search-viewed',
};

const TalentSearch = () => (
  <StaticPageProvider {...StaticPageProps}>
    <Box background="surface">
      <ContentBlock>
        <Box paddingX={{ mobile: 'small', desktop: 'none' }}>
          <Stack space="xlarge">
            <PageIntro title="Connect with the right talent faster">
              <Text size="large">
                Reach out to relevant candidates with instant Candidate Matches
                and Premium Talent Search.
              </Text>
            </PageIntro>
            <Box paddingY="large">
              <Columns space="medium" collapseBelow="desktop">
                <Column width="1/5">
                  <Box className={styles.clock} display={['none', 'block']} />
                </Column>
                <Column>
                  <Stack space="medium">
                    <Heading level="3">
                      Save time by seeing matched candidates
                    </Heading>
                    <Text>
                      We&apos;ll show you a list of candidates that match each
                      role you&apos;re hiring for, so you can start reaching out
                      to people faster.
                    </Text>
                    <Text>
                      Access to unique CV data also ensures our algorithms match
                      the most relevant candidates.
                    </Text>
                  </Stack>
                </Column>
              </Columns>
            </Box>
            <Divider />
            <Columns space="medium" collapseBelow="desktop" alignY="center">
              <Column>
                <Stack space="medium">
                  <Heading level="3">
                    Widen the reach of your job ad instantly
                  </Heading>
                  <Text>
                    Access up to 100 AI-matched candidate profiles with each job
                    ad you post, extending your reach beyond current applicants.
                  </Text>
                </Stack>
              </Column>
              <Column>
                <div className={styles.talentSearchImage1} />
              </Column>
            </Columns>
            <Divider />
            <Box paddingY="large">
              <Columns space="medium" collapseBelow="desktop">
                <Column>
                  <Columns
                    space={{ mobile: 'xxlarge', desktop: 'small' }}
                    collapseBelow="desktop"
                  >
                    <Column>
                      <Box className={styles.iconWrapper}>
                        <Box className={[styles.iconBase, styles.sendJob]} />
                        <Box paddingTop="small">
                          <Stack space="small">
                            <Text weight="strong">Send Job</Text>
                            <Text>25 credits</Text>
                          </Stack>
                        </Box>
                      </Box>
                    </Column>
                    <Column>
                      <Box className={styles.iconWrapper}>
                        <Box
                          className={[styles.iconBase, styles.sendMessage]}
                        />
                        <Box paddingTop="small">
                          <Stack space="small">
                            <Text weight="strong">Send Message</Text>
                            <Text>50 credits</Text>
                          </Stack>
                        </Box>
                      </Box>
                    </Column>
                    <Column>
                      <Box className={styles.iconWrapper}>
                        <Box className={[styles.iconBase, styles.accessCv]} />
                        <Box paddingTop="small">
                          <Stack space="small">
                            <Text weight="strong">Access CV</Text>
                            <Text>200 credits</Text>
                          </Stack>
                        </Box>
                      </Box>
                    </Column>
                  </Columns>
                </Column>
                <Column>
                  <Box paddingTop={{ mobile: 'xxlarge', desktop: 'none' }}>
                    <Stack space="medium">
                      <Heading level="3">
                        Maximize your credits, connect with matched candidates
                      </Heading>
                      <Text>
                        With each job ad, you&apos;ll receive 1,000 credits to
                        access candidates&apos; CV and send job ads or messages
                        to candidates of interest.
                      </Text>
                    </Stack>
                  </Box>
                </Column>
              </Columns>
            </Box>
            <Box
              borderRadius="large"
              paddingX="gutter"
              paddingY="large"
              boxShadow="borderNeutralLight"
            >
              <Columns space="medium" collapseBelow="desktop">
                <Column width="1/5">
                  <Box className={styles.sally} />
                </Column>
                <Column>
                  <Stack space="medium">
                    <Text>
                      &quot;As a recruiter, one of the biggest tools in my
                      candidate sourcing arsenal is the SEEK database. This is a
                      fantastic resource for my colleagues and I to search the
                      resumes of candidates who are actively seeking work, as
                      well as those who were looking in the past and might be
                      ready for a change now.&quot;
                    </Text>
                    <Text>
                      <Strong>Sally Horwood - people2people</Strong>
                    </Text>
                  </Stack>
                </Column>
              </Columns>
            </Box>
            <Box borderRadius="large" paddingY="large">
              <Stack space="medium" align="center">
                <Heading level="3">
                  Find the right candidate for your job sooner
                </Heading>
                <Text size="large">
                  Post your job ad and view your matched candidates
                </Text>
                <CTAButton
                  center={true}
                  text={{
                    loggedIn: 'Create a job ad',
                    loggedOut: 'Register for free',
                  }}
                  href={{
                    loggedIn: CREATE_JOB,
                    loggedOut: REGISTER,
                  }}
                  tracking={{
                    loggedIn: 'create_job_link_clicked',
                    loggedOut: 'register_link_clicked',
                  }}
                  tone="formAccent"
                  variant="ghost"
                />
              </Stack>
            </Box>
            <Divider />
            <Box paddingY="large">
              <Stack space="xxlarge">
                <Columns space="medium" collapseBelow="desktop">
                  <Column>
                    <div className={styles.talentSearchImage2} />
                  </Column>
                  <Column>
                    <Stack space="medium">
                      <Heading level="3">
                        Broaden your search and discover new candidates with
                        SEEK Premium Talent Search.
                      </Heading>
                      <Text>
                        Building on the features of Talent Search, Premium
                        Talent Search also allows you to:
                      </Text>
                      <List>
                        <Text>
                          Search across the entire pool of 15 million searchable
                          SEEK Profiles
                        </Text>
                        <Text>
                          Widen your results beyond the location of the job ad
                        </Text>
                        <Text>
                          Search by job title, skills, company or candidate name
                        </Text>
                        <Text>
                          Easily gauge if a candidate may be interested with our
                          Approachability Signal.
                        </Text>
                      </List>
                    </Stack>
                  </Column>
                </Columns>
                <Box
                  padding="xlarge"
                  textAlign="center"
                  background="neutralLight"
                  borderRadius="large"
                  marginBottom="xxlarge"
                >
                  <Stack space="medium">
                    <Heading level="3">
                      SEEK Premium Talent Search Credit Packs start from $3,250.
                      For more info, get in touch with our sales team on 13 64
                      34
                    </Heading>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </ContentBlock>
    </Box>
  </StaticPageProvider>
);

export default TalentSearch;
